"use client";

import type { WhopElement } from "@whop/embedded-components-vanilla-js/types";

import { useEffect, useMemo, useSyncExternalStore } from "react";

export function useElementSnapshot<E extends WhopElement<any, any, any>>(
	source: E | undefined | null,
): ReturnType<E["getSnapshot"]> | null {
	const callbacks = useMemo(() => {
		return new Set<() => void>();
	}, []);

	const state = useSyncExternalStore(
		(listener) => {
			const onSnapshot = () => {
				listener();
			};
			callbacks.add(onSnapshot);
			return () => {
				callbacks.delete(onSnapshot);
			};
		},
		() => {
			const snap = source?.getSnapshot();
			return snap;
		},
		() => {
			return null;
		},
	);

	useEffect(() => {
		if (!source) {
			return;
		}

		const onSnapshot = () => {
			for (const callback of callbacks) {
				callback();
			}
		};
		source.on("snapshot", onSnapshot);
		return () => {
			source.off("snapshot", onSnapshot);
		};
	}, [source, callbacks]);

	return state;
}
